import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import ssoConfig from './amplify-sso-config';

export default function SSOCallback() {
	const [error, setError] = useState(null);
	const exchangeStarted = useRef(false);

	useEffect(() => {
		// Prevent double-execution in React StrictMode (dev mode mounts twice).
		// Authorization codes are single-use, so the second call would fail.
		if (exchangeStarted.current) return;
		exchangeStarted.current = true;

		async function handleCallback() {
			const params = new URLSearchParams(window.location.search);
			const code = params.get('code');
			const errorParam = params.get('error');
			const errorDescription = params.get('error_description');

			if (errorParam) {
				setError(errorDescription || 'Authentication failed. Please try again.');
				return;
			}

			if (!code) {
				setError('No authorization code received. Please try again.');
				return;
			}

			try {
				// Exchange the authorization code for tokens via the Cognito token endpoint
				const tokenEndpoint = `https://${ssoConfig.oauth.domain}/oauth2/token`;
				const response = await fetch(tokenEndpoint, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
					},
					body: new URLSearchParams({
						grant_type: 'authorization_code',
						client_id: ssoConfig.aws_user_pools_web_client_id,
						code: code,
						redirect_uri: `${window.location.origin}/sso/callback`,
					}).toString(),
				});

				if (!response.ok) {
					const errData = await response.json().catch(() => ({}));
					throw new Error(errData.error_description || 'Token exchange failed');
				}

				const tokens = await response.json();

				// Store tokens in localStorage where Amplify v6 expects them
				const clientId = ssoConfig.aws_user_pools_web_client_id;
				const lastAuthUser = parseJwt(tokens.id_token)?.sub || 'sso-user';

				const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
				localStorage.setItem(`${keyPrefix}.LastAuthUser`, lastAuthUser);
				localStorage.setItem(`${keyPrefix}.${lastAuthUser}.idToken`, tokens.id_token);
				localStorage.setItem(`${keyPrefix}.${lastAuthUser}.accessToken`, tokens.access_token);
				localStorage.setItem(`${keyPrefix}.${lastAuthUser}.refreshToken`, tokens.refresh_token);
				localStorage.setItem(`${keyPrefix}.${lastAuthUser}.clockDrift`, '0');

				// Redirect to the app
				window.location.href = '/';
			} catch (err) {
				console.error('SSO callback error:', err);
				setError(err.message || 'Failed to complete sign-in. Please try again.');
			}
		}

		handleCallback();
	}, []);

	if (error) {
		return (
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
				minHeight="100vh"
				gap={2}
				sx={{ backgroundColor: '#e0e0e0' }}
			>
				<Typography color="error" variant="h6">
					Sign-in failed
				</Typography>
				<Typography sx={{ color: '#666666' }}>{error}</Typography>
				<Button variant="contained" href="/sso">
					Back to SSO Login
				</Button>
			</Box>
		);
	}

	return (
		<Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" gap={2} sx={{ backgroundColor: '#e0e0e0' }}>
			<CircularProgress />
			<Typography sx={{ color: '#333333' }}>Completing sign in...</Typography>
		</Box>
	);
}

// Decode a JWT to extract its payload (no verification, just parsing)
function parseJwt(token) {
	try {
		const base64Url = token.split('.')[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join('')
		);
		return JSON.parse(jsonPayload);
	} catch {
		return null;
	}
}
